import { metricalpEvent } from '@metricalp/react'
import { Add, CheckCircleOutline, Close, ExpandMore, Remove, Warning } from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Slide,
  TextField,
  Typography,
} from '@mui/material'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import { Elements } from '@stripe/react-stripe-js'
import { Appearance, loadStripe, StripeElementsOptions } from '@stripe/stripe-js'
import React, { useEffect, useState } from 'react'
import { SlideProps } from '@mui/material/Slide'
import { useNavigate } from 'react-router-dom'
import { AuthClient } from '../../interactors/clients/AuthClient'
import { backendClient } from '../../interactors/clients/client'
import {
  AdoptionAttemptDto,
  AnimalAdoptionDtoOfferType,
  AnimalDto,
  CustomerDto,
} from '../../interactors/gen/backendClient'
import { useBasketStore } from '../../store/BasketStore'
import { CircularProgressPanel } from '../common/CircularProgressPanel'
import { CheckoutForm } from '../common/StripeCheckoutForm'
import data from '../Shopping/data/products.json'
import { colors, theme } from '../theme'
import { AdoptionDonationEditModal } from './AdoptionDonationEditModal'
import { AdoptionPriceDetailsModal } from './AdoptionPriceDetailsModal'
import { MetricalpInfo } from './DocumentSignCard'
import { InfoBox } from '../common/InfoBox'
import { getProductImageSrc } from '../../utils/S3-links'

interface Props {
  attempt: AdoptionAttemptDto
  animal: AnimalDto
  association: CustomerDto | null
  onSubmit: (
    data: Partial<AdoptionAttemptDto>,
    type: 'certificate' | 'contract' | 'payment',
    noUpdate?: boolean
  ) => Promise<void>
  metricalpInfo: MetricalpInfo
}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY as string)
type ProductType = typeof data.products[number]

const ProductPreviewTransition = React.forwardRef(function Transition(
  props: SlideProps & { children: React.ReactElement<unknown, string> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} timeout={{ enter: 400, exit: 350 }} {...props} />
})

export const AdoptionPaymentPath: React.FC<Props> = ({ animal, attempt, association, onSubmit, metricalpInfo }) => {
  const queryParams = new URLSearchParams(window.location.search)
  const initialStep = queryParams.get('step')
  const navigate = useNavigate()
  const basketStore = useBasketStore()
  const basket = basketStore.basket
  const PAYMENT_FEE_COVERAGE_AMOUNT = 6
  const DEPOSIT = Number(attempt.depositAmount) || 0
  const HAS_DEPOSIT = DEPOSIT > 0 && attempt.depositStatus === true
  const ADOPTION_PRICE = Number(animal.adoption.priceInEuros) || 0
  const isFreeAmount = animal.adoption.offerType === AnimalAdoptionDtoOfferType.FreeAmount
  const freeAmountMinimum = Math.max(ADOPTION_PRICE || 1, HAS_DEPOSIT ? DEPOSIT : 0)
  const freeAmountRemainingMinimum = Math.max(freeAmountMinimum - (HAS_DEPOSIT ? DEPOSIT : 0), 0)
  const initialFreeAmount = HAS_DEPOSIT ? freeAmountRemainingMinimum : freeAmountMinimum
  const [freeAmount, setFreeAmount] = useState<string>(initialFreeAmount.toString())
  const [clientSecret, setClientSecret] = useState<string>('')
  const [donation, setDonation] = useState<number>(10)
  const [donationAmount, setDonationAmount] = useState<number>(
    Math.round(((isFreeAmount ? freeAmountMinimum : ADOPTION_PRICE) / 100) * 10)
  )
  const [coverPaymentFees, setCoverPaymentFees] = useState<boolean>(false)
  const [errored, setErrored] = useState<boolean>(false)
  const [freeAmountError, setFreeAmountError] = useState<string>('')
  const [hasTriedFreeAmountSubmit, setHasTriedFreeAmountSubmit] = useState<boolean>(false)
  const [alreadyPaidAdoptionFees, setAlreadyPaidAdoptionFees] = useState<boolean>(
    queryParams.get('alreadyPaid') === 'true' || false
  )
  const FINALIZATION_STEP = 5

  const animalName = attempt?.newName || animal?.name
  const animalSpecies = (animal?.species || '').toLowerCase().trim()
  const isCat = ['cat', 'chat'].includes(animalSpecies || '')
  const isDog = ['dog', 'chien'].includes(animalSpecies || '')
  const recommendedReferences = isCat
    ? ['FC04', 'FC09', 'FC13', 'FC12', 'FC14']
    : isDog
    ? ['FD06', 'FD04', 'FD05', 'FD14']
    : []
  const recommendedProducts = data.products.filter((product) => recommendedReferences.includes(product.reference))
  const basketItemsForAnimal = basket.filter((item) => item.animalId === (animal?.id || ''))
  const [expandedProducts, setExpandedProducts] = useState<Record<string, boolean>>({})
  const [previewProduct, setPreviewProduct] = useState<ProductType | null>(null)

  const stripeAccountId = association?.stripeAccountId
  const canPayAdoptionByStripe = stripeAccountId !== undefined
  const formatEuroAmount = (amount: number) => Math.round((amount + Number.EPSILON) * 100) / 100
  const paymentFeeContribution = donation === 0 && coverPaymentFees ? PAYMENT_FEE_COVERAGE_AMOUNT : 0
  const totalDonationAmount = donationAmount + paymentFeeContribution
  const isFreeAmountEmpty = freeAmount.trim() === ''
  const freeAmountValue = Number(freeAmount)
  const selectedFreeAmountToPay = !isFreeAmountEmpty && Number.isFinite(freeAmountValue) ? freeAmountValue : 0
  const selectedAdoptionAmount = isFreeAmount
    ? selectedFreeAmountToPay + (HAS_DEPOSIT && !alreadyPaidAdoptionFees ? DEPOSIT : 0)
    : ADOPTION_PRICE
  const isFreeAmountValid = !isFreeAmount || alreadyPaidAdoptionFees || selectedAdoptionAmount >= freeAmountMinimum
  const freeAmountInputMinimum = HAS_DEPOSIT ? freeAmountRemainingMinimum : freeAmountMinimum
  const freeAmountValidationMessage = HAS_DEPOSIT
    ? `Le montant minimum restant à payer est de ${freeAmountRemainingMinimum}€.`
    : `Le montant minimum est de ${freeAmountMinimum}€.`
  const displayedFreeAmountError =
    isFreeAmount && !isFreeAmountValid && (!isFreeAmountEmpty || hasTriedFreeAmountSubmit)
      ? freeAmountValidationMessage
      : freeAmountError
  const adoptionAmountToPay = Math.max(
    (alreadyPaidAdoptionFees ? 0 : selectedAdoptionAmount) - (HAS_DEPOSIT && !alreadyPaidAdoptionFees ? DEPOSIT : 0),
    0
  )

  const total = formatEuroAmount(totalDonationAmount + adoptionAmountToPay)

  const accessoriesTotal = basketItemsForAnimal.reduce((sum, item) => sum + item.quantity * item.price, 0)
  const payAmount = formatEuroAmount(total + accessoriesTotal)
  const isZeroPayAmount = payAmount <= 0.001
  const ctaLabel = isZeroPayAmount ? 'Suivant' : `Payer ${payAmount}€`
  const ctaDisabled = (!isZeroPayAmount && !canPayAdoptionByStripe && !alreadyPaidAdoptionFees) || !isFreeAmountValid
  const fixedCtaSx = {
    fontSize: '18px',
    position: 'fixed' as const,
    bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
    left: { xs: 16, md: '276px' },
    right: 16,
    zIndex: 1300,
    boxShadow: '0 14px 32px rgba(0,0,0,0.18)',
    animation: 'cta-bounce 0.45s ease',
    '@keyframes cta-bounce': {
      '0%': { transform: 'translateY(120%)' },
      '70%': { transform: 'translateY(-6px)' },
      '100%': { transform: 'translateY(0)' },
    },
  }
  const previewSliderSettings = {
    dots: true,
    arrows: false,
    autoplay: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    infinite: previewProduct?.images && previewProduct.images.length > 1,
    speed: 250,
    customPaging: () => <div className="ft-slick__dots--custom"></div>,
  }

  const [step, setStep] = useState<number>(initialStep ? Number(initialStep) : 1)
  const shouldShowFixedCta = step === FINALIZATION_STEP

  const checkIfAlreadyPaidAdoptionFees = async () => {
    const account = await AuthClient.getCurrentAccount()
    if (account && account.adoptionAttempts) {
      const isAdoptionPaid = account.adoptionAttempts.some(
        (adoption) => adoption.id === attempt.id && adoption.paymentDone === true
      )
      return isAdoptionPaid
    }
    return false
  }

  const handleFreeAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFreeAmount(event.target.value)
    setHasTriedFreeAmountSubmit(false)
    setFreeAmountError('')
  }

  const handleAccessPaymentPage = () => {
    setStep(FINALIZATION_STEP)
    navigate(`/adoptions/${attempt.id}?page=action&step=${FINALIZATION_STEP}`)
  }

  const handleStepForward = async () => {
    if (!isFreeAmountValid) {
      setHasTriedFreeAmountSubmit(true)
      setFreeAmountError(freeAmountValidationMessage)
      return
    }

    if (isZeroPayAmount) {
      if (alreadyPaidAdoptionFees) {
        await onSubmit({ step: 4 }, 'payment')
        metricalpEvent({
          type: `adoption_action`,
          action: `done_payment`,
          adopterId: metricalpInfo.adopterId,
          adopterName: metricalpInfo.adopterName,
          animalName: metricalpInfo.animalName,
          animalId: metricalpInfo.animalId,
        })
      }
      return navigate(`/adoptions/${attempt.id}?congrats=gifts`)
    }

    const isAdoptionPaid = await checkIfAlreadyPaidAdoptionFees()
    if (isAdoptionPaid) {
      return window.open(`https://adoptant.petso.fr/adoptions/${attempt.id}`)
    }
    try {
      await createPaymentIntent()
    } catch (e) {
      setStep(1)
      setErrored(true)
      console.error(e)
      return
    }
    setStep(4)
    navigate(`/adoptions/${attempt.id}?page=action&step=${4}`)
  }

  useEffect(() => {
    setStep(initialStep ? Number(initialStep) : 1)
  }, [queryParams])

  const [modalOpened, setModalOpened] = useState<boolean>(false)
  const [donationModalOpened, setDonationModalOpened] = useState<boolean>(false)
  const [insuranceChoice, setInsuranceChoice] = useState<'yes' | 'no' | null>(null)

  const handleDonationChange = (_: React.MouseEvent<HTMLElement>, newDonation: number | null) => {
    if (newDonation !== null) {
      const amount = Math.round((selectedAdoptionAmount / 100) * newDonation)
      setDonation(newDonation)
      setDonationAmount(amount)
      if (newDonation === 0) {
        setCoverPaymentFees(true)
      } else {
        setCoverPaymentFees(false)
      }
    }
  }

  const handleAddProduct = (productReference: string) => {
    const product = data.products.find((p) => p.reference === productReference)
    if (!product) return
    basketStore.addOrUpdateItem({
      reference: product.reference,
      quantity: 1,
      price: product.price,
      animalId: animal?.id || '',
    })
  }

  const handleRemoveProduct = (productReference: string) => {
    const product = data.products.find((p) => p.reference === productReference)
    if (!product) return
    basketStore.reduceItemQuantity(product.reference, 1, animal?.id || '', undefined)
  }

  const toggleProductExpansion = (productReference: string) => {
    setExpandedProducts((prev) => ({ ...prev, [productReference]: !prev[productReference] }))
  }

  const handleOpenPreview = (product: ProductType) => setPreviewProduct(product)
  const handleClosePreview = () => setPreviewProduct(null)

  const createPaymentIntent = async () => {
    const productItems = basketItemsForAnimal.map((item) => ({
      reference: item.reference,
      quantity: item.quantity,
      price: item.price * 100,
    }))
    const productIds = productItems.map((p) => p.reference).join(',')
    const insuranceQuoteValue = insuranceChoice === 'yes' ? 'Yes' : insuranceChoice === 'no' ? 'No' : 'Not_Clicked'
    const insuranceItem = { reference: `INSURANCE_QUOTE_${insuranceQuoteValue.toUpperCase()}`, quantity: 1, price: 0 }
    const response = await backendClient.post(
      `/adopters-platform/stripe/create-payment-intent?attemptId=${attempt.id}&associationStripeAccountId=${stripeAccountId}`,
      {
        items: [
          ...(alreadyPaidAdoptionFees || !canPayAdoptionByStripe || adoptionAmountToPay <= 0
            ? []
            : [{ reference: 'ADOPTION', quantity: 1, price: adoptionAmountToPay * 100 }]),
          { reference: 'DONATION', quantity: 1, price: totalDonationAmount * 100 },
          ...productItems,
          insuranceItem,
        ],
        metadata: {
          productIds,
          InsuranceQuote: insuranceQuoteValue,
          insuranceQuote: insuranceQuoteValue,
        },
        productIds,
        insuranceQuote: insuranceQuoteValue,
      }
    )
    setClientSecret(response.data)
  }

  const appearance: Appearance = {
    theme: 'stripe',
  }

  const options: StripeElementsOptions = {
    clientSecret,
    appearance,
  }

  const Step1 = () => (
    <>
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'start', justifyContent: 'start', py: 4 }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            alignItems: 'start',
            justifyContent: 'start',
            width: '100%',
          }}
        >
          {errored && (
            <InfoBox
              sx={{ mt: -1, p: 1 }}
              messageType="error"
              content={`Votre paiement n'a pas été pris en compte car l'association n'a pas finalisé la configuration du paiement en ligne. Pour continuer, utilisez un autre mode de paiement et cliquez sur le bouton "J'ai déjà effectué le réglement"`}
            />
          )}
          <Box
            sx={{
              backgroundColor: colors.blueSky,
              color: 'white',
              fontSize: '20px',
              fontWeight: 700,
              width: 'fit-content',
              p: 1,
              py: 0.5,
              pr: 2,
              borderRadius: '11px',
              transform: 'rotate(-1deg)',
            }}
          >
            Les frais d&apos;adoption :
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              width: '100%',
              mt: 2,
              px: 1,
            }}
          >
            <Box>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(87, 87, 87, 1)',
                  fontStyle: 'italic',
                  textDecoration: alreadyPaidAdoptionFees ? 'line-through' : 'none',
                }}
              >
                {isFreeAmount
                  ? `Montant libre pour l'adoption de ${animalName} :`
                  : `Frais d'adoption de ${animalName} :`}
              </Typography>
              {isFreeAmount && HAS_DEPOSIT && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(87, 87, 87, 0.82)',
                    display: 'block',
                    fontStyle: 'italic',
                    mt: 0.5,
                  }}
                >
                  - minimum de {freeAmountMinimum}€ incluant l&apos;acompte
                </Typography>
              )}
            </Box>
            <Typography variant="body1" sx={{ color: 'rgba(87, 87, 87, 1)', fontWeight: 700 }}>
              {alreadyPaidAdoptionFees ? 0 : formatEuroAmount(selectedAdoptionAmount)}€
            </Typography>
          </Box>
          {isFreeAmount && !alreadyPaidAdoptionFees && (
            <TextField
              label={
                HAS_DEPOSIT
                  ? `Montant pour l'adoption (min restant : ${freeAmountRemainingMinimum}€)`
                  : `Montant pour l'adoption (min : ${freeAmountMinimum}€)`
              }
              placeholder={
                HAS_DEPOSIT
                  ? `Choisissez un montant restant à payer (min. ${freeAmountRemainingMinimum}€)`
                  : `Choisissez un montant pour l'adoption (min. ${freeAmountMinimum}€)`
              }
              type="number"
              value={freeAmount}
              onChange={handleFreeAmountChange}
              error={false}
              helperText={
                displayedFreeAmountError || (HAS_DEPOSIT ? "L'acompte validé sera déduit automatiquement." : undefined)
              }
              FormHelperTextProps={{
                sx: { color: displayedFreeAmountError ? colors.amber : undefined },
              }}
              inputProps={{
                step: '5',
                inputMode: 'decimal',
                min: freeAmountInputMinimum,
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
              }}
              size="small"
              fullWidth
              sx={{
                mt: 2,
                px: 1,
                ...(displayedFreeAmountError
                  ? {
                      '& .MuiOutlinedInput-root fieldset': { borderColor: colors.amber },
                      '& .MuiOutlinedInput-root:hover fieldset': { borderColor: colors.amber },
                      '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: colors.amber },
                      '& .MuiInputLabel-root': { color: colors.amber },
                      '& .MuiInputLabel-root.Mui-focused': { color: colors.amber },
                    }
                  : {}),
              }}
            />
          )}
          {HAS_DEPOSIT && !alreadyPaidAdoptionFees && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                width: '100%',
                mt: 2,
                px: 1,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(87, 87, 87, 1)',
                  fontStyle: 'italic',
                  textDecoration: alreadyPaidAdoptionFees ? 'line-through' : 'none',
                }}
              >
                Acompte reçu :
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(87, 87, 87, 1)', fontWeight: 700 }}>
                -{DEPOSIT}€
              </Typography>
            </Box>
          )}
          {alreadyPaidAdoptionFees && (
            <Box sx={{ pl: 1, width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start', gap: 1, width: '100%' }}>
                <CheckCircleOutline sx={{ color: colors.seaGreen, fontSize: '26px' }} />
                <Typography variant="body1" color={colors.seaGreen} fontWeight={700} fontSize={14}>
                  Vous affirmez avoir déjà réglé les frais d&apos;adoption. L’association validera votre contribution.{' '}
                  <Button
                    variant="outlined"
                    color="inherit"
                    sx={{ fontStyle: 'italic', color: colors.blueSky, fontWeight: 700, p: '2px' }}
                    onClick={() => setAlreadyPaidAdoptionFees(false)}
                  >
                    Modifier
                  </Button>
                </Typography>
              </Box>
            </Box>
          )}
          {!canPayAdoptionByStripe && !alreadyPaidAdoptionFees && (
            <Box sx={{ pl: 1, width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'start',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                }}
              >
                <Warning sx={{ color: colors.amber, fontSize: '26px' }} />
                <Typography variant="body1" color={'black'} fontWeight={700} fontSize={14}>
                  {`L'association n'a pas encore configuré le paiement en ligne.`}
                </Typography>
              </Box>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'start',
              justifyContent: 'space-between',
              gap: 1,
              width: '100%',
              mt: 2,
              px: 1,
            }}
          >
            <Typography variant="body1" sx={{ color: 'rgba(87, 87, 87, 1)', fontStyle: 'italic' }}>
              Votre contribution à Petso :
              <br />
              <span
                style={{ fontStyle: 'normal', fontWeight: 700, color: theme.palette.primary.main, cursor: 'pointer' }}
                onClick={() => setModalOpened(true)}
              >
                Pourquoi ?
              </span>
              <span style={{ fontStyle: 'normal', fontWeight: 700, color: theme.palette.primary.main }}>{` - `}</span>
              <span
                style={{ fontStyle: 'normal', fontWeight: 700, color: theme.palette.primary.main, cursor: 'pointer' }}
                onClick={() => setDonationModalOpened(true)}
              >
                Modifier
              </span>
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(87, 87, 87, 1)', fontWeight: 700 }}>
              {donationAmount}€
            </Typography>
          </Box>
          {donation === 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                width: '100%',
                mt: 1,
                px: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={coverPaymentFees}
                    onChange={(event) => setCoverPaymentFees(event.target.checked)}
                    color="primary"
                  />
                }
                label="Je participe à hauteur de 6€ dans le cadre de cette adoption"
                sx={{ ml: -1 }}
              />
              <Typography variant="body1" sx={{ color: 'rgba(87, 87, 87, 1)', fontWeight: 700 }}>
                {paymentFeeContribution}€
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              width: '100%',
              mt: 1,
              px: 1,
            }}
          >
            <Typography variant="body1" sx={{ color: 'rgba(87, 87, 87, 1)', fontStyle: 'italic' }}>
              Total :
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(87, 87, 87, 1)', fontWeight: 700 }}>
              {total}€
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            alignItems: 'start',
            justifyContent: 'start',
            width: '100%',
          }}
        >
          {!canPayAdoptionByStripe && !alreadyPaidAdoptionFees && (
            <Typography
              variant="body1"
              color={'black'}
              fontWeight={700}
              fontSize={14}
              sx={{ mt: 1, textAlign: 'center' }}
            >
              {`Pour continuer, utilisez un autre mode de paiement et cliquez sur "J'ai déjà effectué le réglement".`}
            </Typography>
          )}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{
              fontSize: '18px',
              mt: 1.5,
            }}
            disabled={ctaDisabled}
            onClick={isZeroPayAmount ? handleStepForward : handleAccessPaymentPage}
          >
            {isZeroPayAmount ? 'Suivant' : `Accéder au paiement`}
          </Button>
          {!alreadyPaidAdoptionFees && (
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              sx={{ fontSize: '18px' }}
              onClick={() => setAlreadyPaidAdoptionFees(true)}
            >
              {`J'ai déjà effectué le réglement`}
            </Button>
          )}
        </Box>
      </Box>
      <AdoptionPriceDetailsModal open={modalOpened} handleClose={() => setModalOpened(false)} />
      <AdoptionDonationEditModal
        open={donationModalOpened}
        handleClose={() => setDonationModalOpened(false)}
        donation={donation}
        handleDonationChange={handleDonationChange}
      />
    </>
  )

  const Step5 = () => (
    <Box
      sx={{
        py: 4,
        pb: 'calc(140px + env(safe-area-inset-bottom, 0px))',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        alignItems: 'start',
      }}
    >
      <Box
        sx={{
          backgroundColor: colors.blueSky,
          color: 'white',
          fontSize: '20px',
          fontWeight: 700,
          width: 'fit-content',
          p: 1,
          py: 0.5,
          pr: 2,
          borderRadius: '11px',
          transform: 'rotate(-1deg)',
        }}
      >
        Finaliser votre adoption
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 1,
        }}
      >
        <Typography variant="body1" sx={{ color: 'rgba(87, 87, 87, 1)', fontStyle: 'italic' }}>
          Adoption & contribution :
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(87, 87, 87, 1)', fontWeight: 700 }}>
          {payAmount}€
        </Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          backgroundColor: 'white',
          borderRadius: 2,
          border: '1px solid rgba(74, 119, 255, 0.16)',
          boxShadow: '0 10px 28px rgba(7, 41, 72, 0.08)',
          p: 2,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#0D1B2A' }}>
          {`Kozoo vous aide à assurer ${animalName} en toute sérénité`}
        </Typography>
        <Typography sx={{ fontSize: '12px', fontStyle: 'italic', color: '#4C5A6B' }}>
          Souhaitez-vous recevoir un devis gratuit et personnalisé pour sa mutuelle ?{' '}
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.2 }}>
            <Button
              variant="text"
              sx={{ color: colors.yellowSemiLight, fontWeight: 700, fontSize: '12px', textDecoration: 'underline' }}
              onClick={() => toggleProductExpansion('INSURANCE_KOZOO')}
            >
              En savoir plus
            </Button>
            <ExpandMore
              sx={{
                fontSize: '20px',
                color: colors.yellowSemiLight,
                transform: expandedProducts['INSURANCE_KOZOO'] ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          </Box>
        </Typography>
        <Box
          sx={{
            mt: 0.75,
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Button
              variant={insuranceChoice === 'no' ? 'contained' : 'outlined'}
              sx={{
                backgroundColor: insuranceChoice === 'no' ? colors.blueSky : 'white',
                borderColor: colors.blueSky,
                color: insuranceChoice === 'no' ? 'white' : colors.blueSky,
                fontWeight: 700,
                px: 2.5,
                '&:hover': {
                  backgroundColor: insuranceChoice === 'no' ? colors.blueSky : 'rgba(74, 119, 255, 0.06)',
                  borderColor: colors.blueSky,
                },
              }}
              onClick={() => setInsuranceChoice('no')}
            >
              Non
            </Button>
            <Button
              variant={insuranceChoice === 'yes' ? 'contained' : 'outlined'}
              sx={{
                backgroundColor: insuranceChoice === 'yes' ? colors.blueSky : 'white',
                borderColor: colors.blueSky,
                color: insuranceChoice === 'yes' ? 'white' : colors.blueSky,
                fontWeight: 700,
                px: 2.5,
                '&:hover': {
                  backgroundColor: insuranceChoice === 'yes' ? colors.blueSky : 'rgba(74, 119, 255, 0.06)',
                  borderColor: colors.blueSky,
                },
              }}
              onClick={() => setInsuranceChoice('yes')}
            >
              Oui
            </Button>
          </Box>
        </Box>
        <Collapse in={!!expandedProducts['INSURANCE_KOZOO']} timeout="auto" sx={{ width: '100%', mt: 1 }}>
          <Box
            sx={{
              backgroundColor: 'rgba(247, 250, 255, 0.9)',
              border: '1px solid rgba(74, 119, 255, 0.12)',
              borderRadius: 2,
              p: 1.5,
              color: '#0D1B2A',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>
              Mutuelle Kozoo : devis gratuit et sans engagement pour une assurance à la carte :
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, mt: 0.5, color: '#0D1B2A' }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 400 }}>👉 100% protégé</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 400 }}>👉 Remboursement 48H</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 400 }}>👉 Vétérinaires 7/7J</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 400 }}>
                👉 Plus de 100 offres sur-mesure, sans franchise et sans frais cachés
              </Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 400 }}>
                👉 5 plafonds au choix de 1000€ à 3000€, sans restriction par soin
              </Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 400 }}>
                👉 Inscription dès 2 mois, jusqu’à 12 ans révolus & à vie
              </Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 400 }}>
                👉 France métropolitaine, Corse et DROM
              </Typography>
            </Box>
          </Box>
        </Collapse>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, width: '100%' }}>
        <Typography component="h2" variant="h5" sx={{ fontWeight: 700, color: '#0D1B2A', px: 1, mt: 0.5, mb: 0 }}>
          {`Pour accueillir ${animalName}`}
        </Typography>
        <Typography sx={{ fontSize: '12px', color: '#4C5A6B', px: 1, mb: 0.25 }}>
          Livraison gratuite en France métropolitaine, expédiée sous 24h
        </Typography>
        {recommendedProducts.map((product) => {
          const image = getProductImageSrc(product.images[0])
          const basketItem = basket.find(
            (item) => item.reference === product.reference && item.animalId === (animal?.id || '')
          )
          const priceLabel = `${product.price.toLocaleString('fr-FR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} €`
          const isExpanded = expandedProducts[product.reference]
          return (
            <Box
              key={product.reference}
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                pt: 1.25,
                pr: 1.25,
                pl: 1.25,
                pb: 1.25,
                borderRadius: 3,
                border: '1px solid rgba(0, 0, 0, 0.06)',
                boxShadow: '0 16px 38px rgba(0, 0, 0, 0.07)',
                backgroundColor: 'white',
                minHeight: 120,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', width: '100%' }}>
                <Box
                  onClick={() => handleOpenPreview(product)}
                  sx={{
                    width: 88,
                    height: 78,
                    borderRadius: 2,
                    backgroundColor: 'rgba(247, 250, 255, 0.9)',
                    border: '1px solid rgba(74, 119, 255, 0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    flexShrink: 0,
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    component="img"
                    src={image}
                    alt={product.title}
                    sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 0.3 }}>
                  <Typography
                    onClick={() => handleOpenPreview(product)}
                    sx={{ fontWeight: 700, fontSize: '16px', color: '#0D1B2A', cursor: 'pointer' }}
                  >
                    {product.title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '13px', color: '#4C5A6B' }}>{product.brand}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '16px', color: '#0D1B2A' }}>{priceLabel}</Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 1,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => toggleProductExpansion(product.reference)}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      border: '1px solid rgba(0,0,0,0.16)',
                      color: 'rgba(0,0,0,0.56)',
                      backgroundColor: 'white',
                      boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                    }}
                  >
                    <ExpandMore
                      sx={{
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                      }}
                    />
                  </IconButton>
                  {basketItem ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveProduct(product.reference)}
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '50%',
                          border: `1px solid ${colors.blueSky}`,
                          color: colors.blueSky,
                          backgroundColor: 'white',
                          '&:hover': { backgroundColor: 'rgba(105, 141, 172, 0.12)' },
                        }}
                      >
                        <Remove />
                      </IconButton>
                      <Typography sx={{ fontWeight: 700, fontSize: '15px', minWidth: 22, textAlign: 'center' }}>
                        {basketItem.quantity}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleAddProduct(product.reference)}
                        sx={{
                          width: 41,
                          height: 41,
                          borderRadius: '50%',
                          color: 'white',
                          backgroundColor: colors.blueSky,
                          boxShadow: '0 10px 20px rgba(105, 141, 172, 0.35)',
                          '&:hover': { backgroundColor: colors.blueSky },
                        }}
                      >
                        <Add />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Add />}
                      onClick={() => handleAddProduct(product.reference)}
                      sx={{
                        borderRadius: 999,
                        fontWeight: 700,
                        px: 1.5,
                        minHeight: 36,
                        color: 'white',
                        backgroundColor: colors.blueSky,
                        boxShadow: '0 10px 20px rgba(105, 141, 172, 0.35)',
                        '&:hover': { backgroundColor: colors.blueSky },
                      }}
                    >
                      Ajouter
                    </Button>
                  )}
                </Box>
              </Box>
              <Collapse in={isExpanded} timeout="auto" sx={{ width: '100%', mt: 1 }}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(247, 250, 255, 0.9)',
                    border: '1px solid rgba(74, 119, 255, 0.12)',
                    borderRadius: 2,
                    p: 1.5,
                    color: '#0D1B2A',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>Description</Typography>
                  <Typography
                    component="div"
                    sx={{ fontSize: '13px', color: '#3D4A5C', lineHeight: 1.5 }}
                    dangerouslySetInnerHTML={{ __html: product.description }}
                  />
                </Box>
              </Collapse>
            </Box>
          )
        })}
      </Box>
      <Dialog
        open={!!previewProduct}
        onClose={handleClosePreview}
        TransitionComponent={ProductPreviewTransition}
        fullScreen
        PaperProps={{
          sx: {
            position: 'fixed',
            bottom: 0,
            top: '20vh',
            borderRadius: '20px 20px 0 0',
            overflow: 'hidden',
            maxHeight: '80vh',
            m: 0,
            boxShadow: '0 -2px 12px rgba(0,0,0,0.12)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {previewProduct && (
          <DialogContent
            sx={{
              p: 0,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              flex: 1,
              overflowY: 'auto',
              pb: '72px',
            }}
          >
            <Box sx={{ position: 'relative' }}>
              <Slider {...previewSliderSettings}>
                {previewProduct.images.map((img) => (
                  <Box
                    key={img}
                    component="img"
                    src={getProductImageSrc(img)}
                    alt={previewProduct.title}
                    sx={{
                      width: '100%',
                      maxHeight: '260px',
                      objectFit: 'contain',
                      backgroundColor: 'white',
                    }}
                  />
                ))}
              </Slider>
              <IconButton
                onClick={handleClosePreview}
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  bgcolor: 'rgba(0,0,0,0.55)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
              >
                <Close />
              </IconButton>
            </Box>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '20px', color: '#0D1B2A' }}>
                {previewProduct.title}
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#4C5A6B' }}>{previewProduct.brand}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '18px', color: '#0D1B2A' }}>
                  {previewProduct.price.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                  €
                </Typography>
              </Box>
              <Typography
                component="div"
                sx={{ fontSize: '14px', color: '#3D4A5C', lineHeight: 1.5 }}
                dangerouslySetInnerHTML={{ __html: previewProduct.description }}
              />
            </Box>
          </DialogContent>
        )}
        {previewProduct && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              pt: 2,
              px: 2,
              pb: 'calc(24px + env(safe-area-inset-bottom, 0px))',
              backgroundColor: 'white',
              boxShadow: '0 -4px 18px rgba(0,0,0,0.08)',
              borderTop: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ fontSize: '16px', borderRadius: 0 }}
              onClick={() => {
                handleAddProduct(previewProduct.reference)
                handleClosePreview()
              }}
            >
              Ajouter
            </Button>
          </Box>
        )}
      </Dialog>
      {shouldShowFixedCta && (
        <Button variant="contained" color="primary" sx={fixedCtaSx} disabled={ctaDisabled} onClick={handleStepForward}>
          {ctaLabel}
        </Button>
      )}
    </Box>
  )

  const Step4 = () => {
    if (!clientSecret) {
      return <CircularProgressPanel absolute={false} />
    }
    return (
      <Box
        sx={{
          py: 4,
        }}
      >
        <Elements options={options} stripe={stripePromise}>
          <CheckoutForm
            price={payAmount}
            redirectionLink={`https://adoptant.petso.fr/adoptions/${attempt.id}?congrats=gifts`}
            buttonFontSize="18px"
          />
        </Elements>
      </Box>
    )
  }

  const renderStep = () => {
    switch (step) {
      case 4:
        return <Step4 />
      case 5:
        return <Step5 />
      default:
        return <Step1 />
    }
  }

  return renderStep()
}
